import React, { useState } from "react";
import styled from "styled-components";
import { get, some } from "lodash";
import { CirclePlus } from "@sonder/ui/source/icons";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { ButtonList } from "@sonder/ui/source/styled/fragments/ButtonList";
import { CircleButton } from "@sonder/ui/source/styled/fragments/CircleButton";
import {
  FilterDropdownContent,
  FilterEmpty,
  FilterFooter,
  FilterTextArea,
} from "@sonder/ui/source/styled/fragments/GenericFilters";
import { Link } from "@sonder/ui/source/styled/fragments/Link";
import { DropdownContentAlignment } from "@sonder/ui/source/styled/surfaces/DropdownContent";
import { TomTags, useGetTagsQuery } from "core/queries/GetTags";
import { TagFilterGroupItem } from "tasks/components/Filters";

const TagButtonList = styled(ButtonList)`
  min-width: 20rem;
  width: 100%;
`;

interface OnChangeProps {
  addTags: TomTags[];
  removeTags: TomTags[];
}

interface RenderTriggerArgs {
  selectedTags: TomTags[];
  onClear?(): void;
}

export const TagPickerTrigger = () => (
  <CircleButton backgroundVariant="light">
    <CirclePlus />
  </CircleButton>
);

interface TagPickerProps {
  alignContent?: DropdownContentAlignment;
  disabled?: boolean;
  onChange?(data: OnChangeProps): void;
  renderTrigger?: (args: RenderTriggerArgs) => JSX.Element;
  tags?: TomTags[];
  tagType?: string;
}

export const TagPicker = ({
  alignContent = "right",
  onChange = () => undefined,
  renderTrigger = TagPickerTrigger,
  tags = [],
  tagType = "Global",
}: TagPickerProps) => {
  const [search, setSearch] = useState("");
  const { data, loading } = useGetTagsQuery({
    variables: {
      tagType,
    },
  });
  const getIsChecked = (tomTag: TomTags) => {
    return !!some(tags, tomTag);
  };
  const onClickOption = (tomTag: TomTags) => {
    const isChecked = getIsChecked(tomTag);
    if (onChange)
      onChange({
        addTags: !isChecked ? [tomTag] : [],
        removeTags: !isChecked ? [] : [tomTag],
      });
  };
  const onClear = () => {
    if (onChange)
      onChange({
        addTags: [],
        removeTags: tags,
      });
  };
  const tomTags = (get(data, "tomTags") || [])
    .filter(tomTag => {
      if (search === "") return true;

      const isSearched = tomTag.name
        .toLowerCase()
        .includes(search.toLowerCase());
      return getIsChecked(tomTag) || isSearched;
    })
    .sort((a, b) => (a.name < b.name ? -1 : 1))
    .map(tomTag => (
      <TagFilterGroupItem
        isChecked={getIsChecked(tomTag)}
        key={tomTag.id}
        tag={tomTag}
        onClick={() => onClickOption(tomTag)}
      />
    ));
  if (loading) return null;

  const isEmpty = tomTags.length === 0;
  const isEmptyAndSearched = search !== "" && isEmpty;
  const totalSelected = (tags && tags.length) || 0;
  return (
    <Dropdown
      trigger={renderTrigger({
        selectedTags: tags,
        onClear,
      })}
    >
      {({ isOpen: isPortalOpen }) =>
        isPortalOpen && (
          <FilterDropdownContent align={alignContent}>
            <FilterTextArea
              autoFocus
              disabled={loading}
              placeholder={`Search ${tomTags.length} tags`}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {isEmptyAndSearched && (
              <FilterEmpty>{`No Results for ${search}.`}</FilterEmpty>
            )}
            <TagButtonList>{tomTags}</TagButtonList>
            <FilterFooter>
              {`${totalSelected || 0} Selected`}
              {totalSelected > 0 && (
                <Link onClick={onClear}>Clear selected</Link>
              )}
            </FilterFooter>
          </FilterDropdownContent>
        )
      }
    </Dropdown>
  );
};
